// Actions (api.ts): the only writers of the store.
//
//   chooseLeader   set the leader, fly to their capital, raise the current turn's notifications (first time)
//   setDate        a jump: clamped to [EPOCH_START, as-of]; stops autoplay, drops turns in flight and the card stack
//   nextTurn       turns.ts
//   setPlaying     playback.ts
//   select         a hex and its owner (world.countryAtHex)
//
// A jump clears the card stack because its cards describe another moment; the log keeps them.
// A jump and a camera fly also clear the hover card: it names a hex under a pointer that no longer
// points at it, and nothing re-picks until the pointer moves.

import { EPOCH_START } from '../core/dates.ts';
import type { DateStr, LeaderId } from '../core/schema.ts';
import type { Actions } from './api.ts';
import { LIVE_ID_PREFIX } from './notifications.ts';
import { clampSpeed, type Playback } from './playback.ts';
import type { Store } from './state.ts';
import type { Turns } from './turns.ts';

/** What actions need from the world. */
export interface ActionWorld {
  /** Hex of the leader's capital, or null when unknown. */
  capitalHex(leader: LeaderId): number | null;
  countryAtHex(hex: number): string | null;
  hasLens(id: string): boolean;
}

export function clampDate(date: DateStr, asOf: DateStr): DateStr {
  if (date < EPOCH_START) {
    return EPOCH_START;
  }
  return date > asOf ? asOf : date;
}

export function createActions(store: Store, world: ActionWorld, turns: Turns, playback: Playback): Actions {
  let introduced = false;

  function flyTo(hex: number, zoom?: number): void {
    store.hover.value = null;
    store.camera.value = { seq: (store.camera.peek()?.seq ?? 0) + 1, hex, zoom };
  }

  return {
    chooseLeader(id) {
      const changed = store.leader.peek() !== id;
      store.leader.value = id;
      store.selection.value = null;
      turns.jumped();

      // Cards were scored for the previous leader; live cards were not, and are raised only once.
      if (changed) {
        store.notifications.value = store.notifications.peek().filter((n) => n.id.startsWith(LIVE_ID_PREFIX));
      }

      const capital = world.capitalHex(id);
      if (capital !== null) {
        flyTo(capital);
      }
      // The first pick opens with the notifications of the turn the game starts in.
      if (changed && !introduced) {
        introduced = true;
        void turns.catchUp(store.date.peek());
      }
    },

    setDate(date) {
      const next = clampDate(date, store.asOf.peek());
      if (next === store.date.peek()) {
        return;
      }
      playback.stop();
      turns.jumped();
      store.notifications.value = [];
      store.hover.value = null;
      store.date.value = next;
    },

    nextTurn: () => turns.next(),

    setPlaying(on) {
      if (on) {
        playback.start();
        return;
      }
      playback.stop();
    },

    setSpeed(turnsPerSecond) {
      store.speed.value = clampSpeed(turnsPerSecond);
    },

    setLens(id) {
      if (id !== null && !world.hasLens(id)) {
        return;
      }
      store.lens.value = id;
    },

    setMode(mode) {
      store.mode.value = mode;
    },

    select(hex) {
      store.selection.value = hex === null ? null : { hex, iso3: world.countryAtHex(hex) };
    },

    hover(info) {
      store.hover.value = info;
    },

    openPanel(panel, arg) {
      store.panel.value = panel;
      store.panelArg.value = arg ?? null;
    },

    flyTo,

    dismiss(id) {
      store.notifications.value = store.notifications.peek().filter((n) => n.id !== id);
    },
  };
}
